import type { Store } from '@blocksuite/store';

import {
  getCrossDocCellValue,
  isCrossDocRelationServiceInitialized,
} from './database-discovery-bridge';
import { getDatabaseDataSource } from './row-projection';

/**
 * Kontext fuer den bidirektionalen Sync
 */
export interface SyncContext {
  store: Store;
  sourceDatabaseId: string; // Database we're editing from
  sourcePropertyId: string; // Property we're editing (relation column in source DB)
  sourceRowId: string;
  targetDatabaseId: string;
  targetDocId?: string; // For cross-doc relations
  inverseFieldId: string;
  inverseCardinality: 'one' | 'many';
}

/**
 * Info ueber einen erforderlichen Steal
 */
export interface StealInfo {
  currentOwnerId: string;
  currentOwnerDisplay: string;
  targetRowId: string;
  targetRowDisplay: string;
}

/**
 * Prueft ob ein Steal erforderlich ist (bei inverseCardinality='one')
 * Wenn das Ziel bereits einen anderen Owner hat, muss der User bestaetigen
 */
export async function checkStealRequired(
  ctx: SyncContext,
  targetRowId: string
): Promise<StealInfo | null> {
  // Steal nur bei 'one' Cardinality
  if (ctx.inverseCardinality !== 'one') {
    return null;
  }

  // Check if this is a cross-doc relation
  const currentDocId = ctx.store.id;
  const isCrossDoc =
    ctx.targetDocId &&
    ctx.targetDocId !== currentDocId &&
    ctx.targetDocId.length > 0;

  let currentArray: string[];

  if (isCrossDoc && isCrossDocRelationServiceInitialized()) {
    // Cross-doc: use bridge to get cell value
    const result = await getCrossDocCellValue(
      ctx.targetDocId!,
      ctx.targetDatabaseId,
      targetRowId,
      ctx.inverseFieldId
    );
    currentArray = result.success ? (result.value ?? []) : [];
  } else {
    // Local: use local store
    const targetDataSource = getDatabaseDataSource(
      ctx.store,
      ctx.targetDatabaseId
    );
    if (!targetDataSource) {
      return null;
    }

    const currentValue = targetDataSource.cellValueGet(
      targetRowId,
      ctx.inverseFieldId
    );
    currentArray = normalizeToArray(currentValue);
  }

  // Kein Owner -> kein Steal
  if (currentArray.length === 0) {
    return null;
  }

  const currentOwnerId = currentArray[0];

  // Bereits der Owner -> kein Steal
  if (currentOwnerId === ctx.sourceRowId) {
    return null;
  }

  // Steal erforderlich
  // Note: currentOwnerId is a row in the SOURCE database (where we're editing)
  // targetRowId is a row in the TARGET database (where relation points to)
  return {
    currentOwnerId,
    currentOwnerDisplay: getRowDisplay(
      ctx.store,
      ctx.sourceDatabaseId, // currentOwner is in source DB
      currentOwnerId
    ),
    targetRowId,
    targetRowDisplay: getRowDisplay(
      ctx.store,
      ctx.targetDatabaseId, // target is in target DB
      targetRowId
    ),
  };
}

/**
 * Verarbeitet Aenderungen an einer Relation-Zelle
 * Synchronisiert automatisch das inverse Feld wenn konfiguriert
 *
 * @param targetDocId - Optional target doc ID for cross-doc relations
 */
export async function processRelationChange(
  store: Store,
  sourceDatabaseId: string,
  sourcePropertyId: string,
  sourceRowId: string,
  targetDatabaseId: string,
  inverseFieldId: string | undefined,
  inverseCardinality: 'one' | 'many',
  oldValue: string[],
  newValue: string[],
  showStealDialog: (info: StealInfo) => Promise<boolean>,
  targetDocId?: string
): Promise<string[]> {
  if (!inverseFieldId) {
    throw new Error(
      'Cannot update relation cell without an inverse relation field.'
    );
  }

  const ctx: SyncContext = {
    store,
    sourceDatabaseId,
    sourcePropertyId,
    sourceRowId,
    targetDatabaseId,
    targetDocId,
    inverseFieldId,
    inverseCardinality,
  };

  const source = getDatabaseDataSource(store, sourceDatabaseId);
  if (!source)
    throw new Error(`Source database not found: ${sourceDatabaseId}`);
  const finalValue = newValue.filter(id => oldValue.includes(id));
  const edits = new Map<string, string[]>();
  for (const rowId of newValue.filter(id => !oldValue.includes(id))) {
    const steal = await checkStealRequired(ctx, rowId);
    if (steal && !(await showStealDialog(steal))) continue;
    if (steal) {
      const value =
        edits.get(steal.currentOwnerId) ??
        normalizeToArray(
          source.cellValueGet(steal.currentOwnerId, sourcePropertyId)
        );
      edits.set(
        steal.currentOwnerId,
        value.filter(id => id !== rowId)
      );
    }
    finalValue.push(rowId);
  }
  edits.set(sourceRowId, [...new Set(finalValue)]);
  await source.commitCellValues(
    [...edits].map(([rowId, value]) => ({
      rowId,
      propertyId: sourcePropertyId,
      value,
    }))
  );
  return [...new Set(finalValue)];
}

/**
 * Cleanup references to deleted rows in a relation column.
 * Called when we need to remove "orphaned" references after a row deletion.
 *
 * This function:
 * 1. Gets all relation values in the specified column
 * 2. Checks if each referenced row still exists
 * 3. Removes references to deleted rows
 * 4. Syncs the removal to the inverse side (cross-doc aware)
 */
export async function cleanupDeletedRowReferences(
  store: Store,
  sourceDatabaseId: string,
  sourcePropertyId: string,
  targetDatabaseId: string,
  targetDocId: string | undefined,
  _inverseFieldId: string | undefined,
  _inverseCardinality: 'one' | 'many'
): Promise<number> {
  const sourceDataSource = getDatabaseDataSource(store, sourceDatabaseId);
  if (!sourceDataSource) {
    return 0;
  }

  // Check if target exists (for cross-doc, we can't easily check)
  const isCrossDoc =
    targetDocId && targetDocId !== store.id && targetDocId.length > 0;

  let cleanedCount = 0;
  const rowIds = sourceDataSource.getLogicalRowIdsSnapshot();

  for (const rowId of rowIds) {
    const cellValue = sourceDataSource.cellValueGet(rowId, sourcePropertyId);
    const relationArray = normalizeToArray(cellValue);

    if (relationArray.length === 0) continue;

    // For local target DB, check which rows still exist
    let validRelations: string[] = relationArray;
    let removedRelations: string[] = [];

    if (!isCrossDoc) {
      const targetDataSource = getDatabaseDataSource(store, targetDatabaseId);
      if (targetDataSource) {
        validRelations = relationArray.filter(id =>
          targetDataSource.rowExists(id)
        );
        removedRelations = relationArray.filter(
          id => !targetDataSource.rowExists(id)
        );
      }
    }

    if (removedRelations.length > 0) {
      await sourceDataSource.commitCellValues([
        { rowId, propertyId: sourcePropertyId, value: validRelations },
      ]);
      cleanedCount += removedRelations.length;
    }
  }

  if (cleanedCount > 0) {
    console.log('[cleanupDeletedRowReferences] Cleaned up:', {
      sourceDatabaseId,
      sourcePropertyId,
      cleanedCount,
    });
  }

  return cleanedCount;
}

function normalizeToArray(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [String(value)];
}

function getRowDisplay(
  store: Store,
  databaseId: string,
  rowId: string
): string {
  const dataSource = getDatabaseDataSource(store, databaseId);
  if (!dataSource) {
    // Cross-doc: Database not in current store - just return truncated ID
    console.log(
      '[sync] getRowDisplay: Database',
      databaseId,
      'not in current store (cross-doc)'
    );
    return rowId.slice(0, 8);
  }

  // Try title first (special property)
  try {
    const titleValue = dataSource.cellValueGet(rowId, 'title');
    if (titleValue) {
      // Title is a Y.Text object, convert to string
      const text = titleValue.toString?.() ?? String(titleValue);
      if (text && text !== '[object Object]') {
        return text;
      }
    }

    // Fallback: try first text property through the bounded property window.
    const properties = dataSource.getBoundedPropertyIdsWindow().propertyIds;
    for (const propId of properties) {
      const propType = dataSource.propertyTypeGet(propId);
      if (propType === 'text' || propType === 'rich-text') {
        const value = dataSource.cellValueGet(rowId, propId);
        if (value && typeof value === 'string') {
          return value;
        }
      }
    }
  } catch (e) {
    console.warn('[sync] getRowDisplay error:', e);
  }

  return rowId.slice(0, 8);
}
